import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { routeMapRun } from "../../utils/routeMapPoints";
import type { RouteMapRun } from "../../utils/routeMapPoints";
import { routeStopsOf } from "../../utils/containerLineSteps";

/**
 * A container's run and what each of its stops is called.
 *
 * The naming is the same rule the stop cards use — a custom stop wears the
 * label the dispatcher typed, or the name of the kind of stop it is — and it
 * lives here so the container's page and the board's preview cannot drift into
 * calling the same stop two different things.
 */
export function useRouteMapStops(line: Parameters<typeof routeStopsOf>[0]): RouteMapRun & {
  /** What each stop *is* — "Loading site", "Terminal drop-off". */
  titles: Record<string, string>;
  /** Where each stop *is* — "Peshawar". Only stops that have one appear. */
  places: Record<string, string>;
} {
  const { t } = useTranslation("containerOrders");

  return useMemo(() => {
    const run = routeMapRun(line);
    const titles: Record<string, string> = {};
    const places: Record<string, string> = {};

    for (const stop of run.stops) {
      titles[stop.id] =
        stop.role === "custom"
          ? stop.label || t(`route.stopKinds.${stop.kind ?? "other"}`)
          : t(`route.${stop.role}`);

      if (stop.place) places[stop.id] = stop.place;
    }

    return { ...run, titles, places };
  }, [line, t]);
}
